/**
 * DeleteWithReasonDialog — v109.9.103
 *
 * Universal confirm-with-reason dialog used whenever a page deletes a
 * record that goes into the Admin-Hub Recycle Bin. The reason is
 * captured in the DELETE request body so the Master Admin sees exactly
 * WHY every soft-deleted row was removed.
 *
 * Usage:
 *
 *   const [target, setTarget] = useState(null);
 *   <Button onClick={() => setTarget(row)}>Delete</Button>
 *   <DeleteWithReasonDialog
 *     open={!!target}
 *     onClose={() => setTarget(null)}
 *     title={`Delete "${target?.name}"?`}
 *     description="This record will be moved to the Admin Hub → Recycle Bin. Master Admin can restore it from there."
 *     onConfirm={async (reason) => {
 *       await api.delete(`/records/${target.id}`, { data: { reason } });
 *       onDeleted();
 *     }}
 *   />
 *
 * The dialog handles: mandatory-reason toggle (default: optional but
 * strongly encouraged), busy state, keyboard confirmation, autofocus,
 * dark-mode contrast, and testids.
 */
import { useEffect, useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

export default function DeleteWithReasonDialog({
  open,
  onClose,
  onConfirm,
  title = "Delete this record?",
  description = "It will move to the Admin Hub → Recycle Bin. Master Admin can restore or permanently delete it later.",
  confirmLabel = "Delete",
  reasonRequired = false,
  placeholder = "Optional but recommended: why are you deleting this?",
  testid = "delete-with-reason",
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setReason(""); setBusy(false); } }, [open]);

  const submit = async () => {
    const trimmed = reason.trim();
    if (reasonRequired && !trimmed) {
      toast.error("Please add a reason before deleting");
      return;
    }
    setBusy(true);
    try {
      await onConfirm(trimmed);
      onClose?.();
    } catch (e) {
      // Caller shows its own toast via formatError — nothing to do here.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose?.()}>
      <DialogContent className="max-w-md w-[calc(100vw-2rem)] [&>*]:min-w-0 overflow-hidden" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-start gap-2 pr-6 text-left text-base leading-snug text-rose-700 dark:text-rose-300">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span className="min-w-0 break-words">{title}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-[12px] text-slate-600 dark:text-slate-300 leading-relaxed" data-testid={`${testid}-description`}>
            {description}
          </p>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">
              Reason {reasonRequired ? "*" : "(optional)"}
            </Label>
            <Textarea
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={placeholder}
              rows={3}
              className="mt-1 w-full resize-none"
              data-testid={`${testid}-input`}
              maxLength={500}
            />
            <div className="text-[10px] text-slate-400 mt-1 text-right">{reason.length}/500</div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={busy} onClick={onClose} data-testid={`${testid}-cancel`}>Cancel</Button>
          <Button
            disabled={busy || (reasonRequired && !reason.trim())}
            onClick={submit}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid={`${testid}-confirm`}
          >
            <Trash2 size={13} className="mr-1.5" />
            {busy ? "Deleting…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
